import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment,
  InputLabel, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, Paper
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, PictureAsPdf as PdfIcon, Send as SendIcon,
  QrCode2 as QrIcon, ReceiptLong as ReceiptIcon, Block as BlockIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { collection, onSnapshot, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../firebase/config';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

const fmtMoney = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const fmtDateTime = (ts) => {
  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  return d ? d.toLocaleString('de-DE') : '—';
};

/** Datum+Uhrzeit für <input type="datetime-local"> (lokale Zeit, nicht UTC). */
const toLocalInput = (date) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
};

const emptyForm = () => {
  const start = new Date();
  start.setHours(18, 0, 0, 0);
  const end = new Date(start.getTime() + 9 * 60 * 60 * 1000); // bis 03:00 Uhr
  return {
    id: null,
    name: '',
    address: '',
    lat: null,
    lng: null,
    radiusMeters: 300,
    start: toLocalInput(start),
    end: toLocalInput(end),
    direction: 'from',
    maxRides: '',
    maxFarePerRideEur: '',
    eventBudgetEur: '',
    maxDestinationKm: '',
    verifyDriverLocation: true,
    requireGuestName: true,
    requireSignature: true,
    requireDestination: true,
    separateInvoice: true,
    active: true
  };
};

const DIRECTION_LABELS = {
  from: 'Heimfahrten (ab dem Veranstaltungsort)',
  to: 'Hinfahrten (zum Veranstaltungsort)',
  both: 'Hin- und Heimfahrten'
};

/**
 * Firmen-Events eines Rechnungskunden (Konzept §7): anlegen, QR-Aushang
 * erzeugen und der Firma mailen, Nutzung verfolgen, abschalten.
 */
const CompanyEventsDialog = ({ invoiceCustomer, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [usageFor, setUsageFor] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const baseUrl = useMemo(() => window.location.origin, []);
  const publicUrl = useCallback((code) => `${baseUrl}/aktion/${code}`, [baseUrl]);

  useEffect(() => {
    const q = query(collection(db, 'companyEvents'), where('invoiceCustomerId', '==', invoiceCustomer.id));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.window?.start?.seconds || 0) - (a.window?.start?.seconds || 0));
      setEvents(rows);
      setLoading(false);
    }, () => setLoading(false));
  }, [invoiceCustomer.id]);

  const openCreate = () => { setForm(emptyForm()); setFormOpen(true); };
  const openEdit = (ev) => {
    const s = ev.window?.start?.toDate ? ev.window.start.toDate() : new Date();
    const e = ev.window?.end?.toDate ? ev.window.end.toDate() : new Date();
    setForm({
      id: ev.id,
      name: ev.name || '',
      address: ev.location?.address || '',
      lat: ev.location?.lat ?? null,
      lng: ev.location?.lng ?? null,
      radiusMeters: ev.location?.radiusMeters ?? 300,
      start: toLocalInput(s),
      end: toLocalInput(e),
      direction: ev.direction || 'from',
      maxRides: ev.limits?.maxRides ?? '',
      maxFarePerRideEur: ev.limits?.maxFarePerRideEur ?? '',
      eventBudgetEur: ev.limits?.eventBudgetEur ?? '',
      maxDestinationKm: ev.limits?.maxDestinationKm ?? '',
      verifyDriverLocation: ev.requirements?.verifyDriverLocation !== false,
      requireGuestName: ev.requirements?.requireGuestName !== false,
      requireSignature: ev.requirements?.requireSignature !== false,
      requireDestination: ev.requirements?.requireDestination !== false,
      separateInvoice: ev.separateInvoice !== false,
      active: ev.active !== false
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await httpsCallable(functions, 'saveCompanyEvent')({
        id: form.id || undefined,
        invoiceCustomerId: invoiceCustomer.id,
        name: form.name.trim(),
        location: {
          address: form.address.trim(),
          lat: form.lat,
          lng: form.lng,
          radiusMeters: Number(form.radiusMeters) || 300
        },
        window: {
          start: new Date(form.start).toISOString(),
          end: new Date(form.end).toISOString()
        },
        direction: form.direction,
        limits: {
          maxRides: form.maxRides === '' ? null : Number(form.maxRides),
          maxFarePerRideEur: form.maxFarePerRideEur === '' ? null : Number(form.maxFarePerRideEur),
          eventBudgetEur: form.eventBudgetEur === '' ? null : Number(form.eventBudgetEur),
          maxDestinationKm: form.maxDestinationKm === '' ? null : Number(form.maxDestinationKm)
        },
        requirements: {
          verifyDriverLocation: form.verifyDriverLocation,
          requireGuestName: form.requireGuestName,
          requireSignature: form.requireSignature,
          requireDestination: form.requireDestination
        },
        separateInvoice: form.separateInvoice,
        active: form.active
      });
      setFormOpen(false);
    } catch (e) {
      setMsg({ severity: 'error', message: e?.message || 'Event konnte nicht gespeichert werden.' });
    } finally { setSaving(false); }
  };

  /** Aushang erzeugen — optional gleich an die Firma mailen. */
  const poster = async (ev, send) => {
    setBusyId(ev.id); setMsg(null);
    try {
      const res = await httpsCallable(functions, 'sendCompanyEventPoster')({
        eventId: ev.id,
        baseUrl,
        ...(send ? { to: invoiceCustomer.billing?.email || '' } : {})
      });
      if (send) {
        setMsg({ severity: 'success', message: `Aushang an ${res.data?.to} versendet.` });
      } else {
        const url = await getDownloadURL(storageRef(storage, res.data.pdfPath));
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      setMsg({ severity: 'error', message: e?.message || 'Aushang konnte nicht erzeugt werden.' });
    } finally { setBusyId(null); }
  };

  const toggleActive = async (ev) => {
    setBusyId(ev.id);
    try {
      await httpsCallable(functions, 'saveCompanyEvent')({
        id: ev.id,
        invoiceCustomerId: invoiceCustomer.id,
        name: ev.name,
        location: ev.location,
        window: {
          start: ev.window.start.toDate().toISOString(),
          end: ev.window.end.toDate().toISOString()
        },
        direction: ev.direction,
        limits: ev.limits,
        requirements: ev.requirements,
        separateInvoice: ev.separateInvoice !== false,
        active: ev.active === false
      });
    } catch (e) {
      setMsg({ severity: 'error', message: e?.message || 'Status konnte nicht geändert werden.' });
    } finally { setBusyId(null); }
  };

  /** Rechnung nur für diese Aktion erstellen (§7.6). */
  const invoiceEvent = async (ev) => {
    setBusyId(ev.id); setMsg(null);
    try {
      const res = await httpsCallable(functions, 'createInvoiceNow')({
        invoiceCustomerId: invoiceCustomer.id,
        companyEventId: ev.id
      });
      setMsg({ severity: 'success', message: `Rechnung ${res.data?.invoiceNumber || ''} für „${ev.name}" erstellt.` });
    } catch (e) {
      setMsg({ severity: 'error', message: e?.message || 'Rechnung konnte nicht erstellt werden.' });
    } finally { setBusyId(null); }
  };

  /** Fahrten + abgelehnte Scans einer Aktion (Kontrolle, §7.3). */
  const openUsage = async (ev) => {
    setUsageFor(ev); setUsageRows([]); setUsageLoading(true);
    try {
      const [rides, redemptions] = await Promise.all([
        getDocs(query(collection(db, 'rides'), where('companyEventId', '==', ev.id), limit(200))),
        getDocs(query(
          collection(db, 'companyEventRedemptions'),
          where('eventId', '==', ev.id),
          orderBy('scannedAt', 'desc'),
          limit(100)
        )).catch(() => null)
      ]);
      const rideRows = rides.docs.map((d) => ({ kind: 'ride', id: d.id, ...d.data() }));
      const rejected = (redemptions?.docs || [])
        .map((d) => ({ kind: 'scan', id: d.id, ...d.data() }))
        .filter((r) => r.result === 'rejected' || r.locationOk === false);
      setUsageRows([...rideRows, ...rejected]);
    } finally { setUsageLoading(false); }
  };

  const formInvalid = !form.name.trim() || !form.address.trim() || form.lat == null ||
    !form.start || !form.end || new Date(form.end) <= new Date(form.start);

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Events — {invoiceCustomer.displayName || invoiceCustomer.companyName}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Für eine Veranstaltung (z. B. Weihnachtsfeier) erzeugt ein Event einen QR-Code.
            Die Firma verteilt ihn an ihre Gäste, der Fahrer scannt ihn — die Fahrt läuft
            über die Firmenrechnung. Der Code kann jederzeit abgeschaltet werden.
          </Alert>
          {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.message}</Alert>}

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>Event anlegen</Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Zeitraum</TableCell>
                  <TableCell>Ort</TableCell>
                  <TableCell align="right">Genutzt</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={22} sx={{ my: 2 }} /></TableCell></TableRow>
                )}
                {!loading && events.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Noch kein Event — oben „Event anlegen".
                    </Typography>
                  </TableCell></TableRow>
                )}
                {events.map((ev) => {
                  const start = ev.window?.start?.toDate ? ev.window.start.toDate() : null;
                  const end = ev.window?.end?.toDate ? ev.window.end.toDate() : null;
                  const running = ev.active !== false && start && end &&
                    Date.now() >= start.getTime() && Date.now() <= end.getTime();
                  const over = end && Date.now() > end.getTime();
                  const budget = ev.limits?.eventBudgetEur;
                  const used = Number(ev.usage?.amountEur || 0);
                  return (
                    <TableRow key={ev.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{ev.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Code {ev.code} · {DIRECTION_LABELS[ev.direction] || ev.direction}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{fmtDateTime(ev.window?.start)}</Typography>
                        <Typography variant="caption" color="text.secondary">bis {fmtDateTime(ev.window?.end)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ev.location?.address}</Typography>
                        <Typography variant="caption" color="text.secondary">{ev.location?.radiusMeters} m Umkreis</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {ev.usage?.rideCount || 0}{ev.limits?.maxRides ? ` / ${ev.limits.maxRides}` : ''} Fahrten
                        </Typography>
                        <Typography variant="caption" color={budget && used > budget * 0.8 ? 'error' : 'text.secondary'}>
                          {fmtMoney(used)}{budget ? ` / ${fmtMoney(budget)}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {ev.active === false
                          ? <Chip size="small" label="abgeschaltet" color="default" variant="outlined" />
                          : running
                            ? <Chip size="small" label="läuft" color="success" />
                            : over
                              ? <Chip size="small" label="beendet" variant="outlined" />
                              : <Chip size="small" label="geplant" color="info" variant="outlined" />}
                        {ev.email?.status === 'sent' && (
                          <Tooltip title={`Aushang gesendet an ${ev.email.to}`}>
                            <Chip size="small" label="versendet" color="success" variant="outlined" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="QR-Code anzeigen">
                          <IconButton size="small" onClick={() => setQrFor(ev)}><QrIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Aushang als PDF">
                          <span><IconButton size="small" disabled={busyId === ev.id} onClick={() => poster(ev, false)}>
                            {busyId === ev.id ? <CircularProgress size={16} /> : <PdfIcon fontSize="small" />}
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title={invoiceCustomer.billing?.email
                          ? `Aushang an ${invoiceCustomer.billing.email} senden`
                          : 'Keine Rechnungs-E-Mail hinterlegt'}>
                          <span><IconButton size="small" color="primary"
                            disabled={busyId === ev.id || !invoiceCustomer.billing?.email}
                            onClick={() => poster(ev, true)}>
                            <SendIcon fontSize="small" />
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Fahrten und abgelehnte Scans">
                          <IconButton size="small" onClick={() => openUsage(ev)}><ReceiptIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => openEdit(ev)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={ev.active === false ? 'Wieder aktivieren' : 'Sofort abschalten'}>
                          <span><IconButton size="small" color={ev.active === false ? 'success' : 'warning'}
                            disabled={busyId === ev.id} onClick={() => toggleActive(ev)}>
                            <BlockIcon fontSize="small" />
                          </IconButton></span>
                        </Tooltip>
                        {ev.separateInvoice !== false && (ev.usage?.rideCount || 0) > 0 && (
                          <Tooltip title="Rechnung für dieses Event erstellen">
                            <span><IconButton size="small" color="primary" disabled={busyId === ev.id}
                              onClick={() => invoiceEvent(ev)}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton></span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Anlegen / Bearbeiten — der APIProvider ist Voraussetzung dafür, dass
          PlaceAutocomplete Google-Vorschläge zeigt (wie im Fahrtformular). */}
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Event bearbeiten' : 'Event anlegen'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Name des Events" value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Weihnachtsfeier 2026" required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Richtung</InputLabel>
                <Select label="Richtung" value={form.direction}
                  onChange={(e) => setForm((p) => ({ ...p, direction: e.target.value }))}>
                  {Object.entries(DIRECTION_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PlaceAutocomplete
                label="Veranstaltungsort"
                required
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value, lat: null, lng: null }))}
                onPlaceSelect={(address, location) => setForm((p) => ({
                  ...p,
                  address,
                  // Das Autocomplete liefert {latitude, longitude} (nicht lat/lng).
                  lat: location?.latitude ?? null,
                  lng: location?.longitude ?? null
                }))}
                helperText={form.lat == null ? 'Bitte eine Adresse aus der Liste wählen' : ' '}
                placeholder="Straße, Hausnummer, PLZ, Ort"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="datetime-local" label="Gültig ab" value={form.start}
                onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="datetime-local" label="Gültig bis" value={form.end}
                onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="number" label="Umkreis" value={form.radiusMeters}
                onChange={(e) => setForm((p) => ({ ...p, radiusMeters: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                InputLabelProps={{ shrink: true }}
                helperText="Darin gilt der Ort als erreicht" />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider sx={{ mt: 1 }}><Typography variant="caption">Grenzen</Typography></Divider></Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="number" label="Max. Fahrten" value={form.maxRides}
                onChange={(e) => setForm((p) => ({ ...p, maxRides: e.target.value }))} placeholder="unbegrenzt" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="number" label="Max. je Fahrt" value={form.maxFarePerRideEur}
                onChange={(e) => setForm((p) => ({ ...p, maxFarePerRideEur: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="number" label="Gesamtbudget" value={form.eventBudgetEur}
                onChange={(e) => setForm((p) => ({ ...p, eventBudgetEur: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="number" label="Max. Entfernung" value={form.maxDestinationKm}
                onChange={(e) => setForm((p) => ({ ...p, maxDestinationKm: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
                helperText="Ziel vom Veranstaltungsort" />
            </Grid>

            <Grid size={{ xs: 12 }}><Divider sx={{ mt: 1 }}><Typography variant="caption">Was der Fahrer erfassen muss</Typography></Divider></Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel control={<Switch checked={form.verifyDriverLocation}
                onChange={(e) => setForm((p) => ({ ...p, verifyDriverLocation: e.target.checked }))} />}
                label="Standort des Fahrers prüfen" />
              <FormControlLabel control={<Switch checked={form.requireDestination}
                onChange={(e) => setForm((p) => ({ ...p, requireDestination: e.target.checked }))} />}
                label="Zieladresse vor der Fahrt" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel control={<Switch checked={form.requireGuestName}
                onChange={(e) => setForm((p) => ({ ...p, requireGuestName: e.target.checked }))} />}
                label="Name des Fahrgasts" />
              <FormControlLabel control={<Switch checked={form.requireSignature}
                onChange={(e) => setForm((p) => ({ ...p, requireSignature: e.target.checked }))} />}
                label="Unterschrift am Ziel" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel control={<Switch checked={form.separateInvoice}
                onChange={(e) => setForm((p) => ({ ...p, separateInvoice: e.target.checked }))} />}
                label="Eigene Rechnung für dieses Event (statt Monats-Sammelrechnung)" />
              <FormControlLabel control={<Switch checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />}
                label="Event aktiv" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" onClick={save} disabled={saving || formInvalid}>
            {saving ? 'Speichert…' : form.id ? 'Änderungen speichern' : 'Event anlegen'}
          </Button>
        </DialogActions>
      </Dialog>
      </APIProvider>

      {/* QR-Vorschau */}
      <Dialog open={Boolean(qrFor)} onClose={() => setQrFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{qrFor?.name}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
            {qrFor && <QRCodeSVG value={publicUrl(qrFor.code)} size={220} level="H" />}
            <Typography variant="h6" letterSpacing={2}>{qrFor?.code}</Typography>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Gäste scannen den Code und zeigen ihn dem Fahrer. Zum Abtippen dient der Code darunter.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrFor(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Nutzung */}
      <Dialog open={Boolean(usageFor)} onClose={() => setUsageFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Nutzung — {usageFor?.name}</DialogTitle>
        <DialogContent dividers>
          {usageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : usageRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Noch keine Nutzung.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Zeit</TableCell>
                  <TableCell>Fahrer</TableCell>
                  <TableCell>Fahrgast / Ziel</TableCell>
                  <TableCell align="right">Betrag</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usageRows.map((r) => (
                  <TableRow key={`${r.kind}-${r.id}`} hover>
                    <TableCell>{fmtDateTime(r.kind === 'ride' ? (r.completedTimestamp || r.timestamp) : r.scannedAt)}</TableCell>
                    <TableCell>{r.driverName || '—'}</TableCell>
                    <TableCell>
                      {r.kind === 'ride' ? (
                        <>
                          <Typography variant="body2">{r.eventGuestName || 'ohne Namen'}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.destination || 'ohne Ziel'}</Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">{r.reason || 'Standort abweichend'}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{r.kind === 'ride' ? fmtMoney(r.fare) : '—'}</TableCell>
                    <TableCell>
                      {r.kind === 'scan'
                        ? <Chip size="small" color={r.result === 'rejected' ? 'error' : 'warning'}
                            label={r.result === 'rejected' ? 'abgelehnt' : 'Standort abweichend'} variant="outlined" />
                        : <Chip size="small" label={r.invoiceBillingStatus || r.status} variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageFor(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyEventsDialog;
